import React, { useEffect, useState } from 'react';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { Wrench, ChevronDown, ChevronRight, CheckCircle, Loader, Inbox, Copy, Mail, X } from 'lucide-react';
import { formatDate } from '../utils';

const STATUS_LABELS = {
  pending: 'Venter',
  in_progress: 'Pågår',
  complete: 'Fullført',
  completed: 'Fullført',
  done: 'Fullført',
  failed: 'Feilet',
  cancelled: 'Avbrutt',
};

export default function BuildQueue() {
  const toast = useToast();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notAvailable, setNotAvailable] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [rowForm, setRowForm] = useState({}); // { [id]: { retell_agent_id, assigned_phone, notes } }
  const [completing, setCompleting] = useState(null);
  const [activationPanel, setActivationPanel] = useState(null); // { company_name, email, password }
  const [credCopied, setCredCopied] = useState(false);

  const loadTasks = async () => {
    setLoading(true);
    setNotAvailable(false);
    try {
      const { data } = await api.get('/api/admin/build-queue');
      setTasks(data.tasks || data || []);
    } catch (err) {
      if (err.response?.status === 404) {
        setNotAvailable(true);
        setTasks([]);
      } else {
        toast.error('Kunne ikke laste build queue');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTasks(); }, []); // eslint-disable-line

  const toggleExpand = (id) => {
    setExpandedId(prev => prev === id ? null : id);
    setRowForm(prev => ({ ...prev, [id]: prev[id] || { retell_agent_id: '', assigned_phone: '', notes: '' } }));
  };

  const updateField = (id, field, value) => {
    setRowForm(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const handleComplete = async (id) => {
    setCompleting(id);
    try {
      const { data } = await api.patch(`/api/admin/build-queue/${id}/complete`, rowForm[id] || {});
      toast.success('Oppgave markert som fullført');
      setExpandedId(null);
      if (data?.credentials?.email && data?.credentials?.password) {
        setActivationPanel({
          company_name: data.company_name || '',
          email: data.credentials.email,
          password: data.credentials.password,
        });
      }
      loadTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke fullføre oppgaven');
    } finally {
      setCompleting(null);
    }
  };

  const copyCred = (text) => {
    navigator.clipboard.writeText(text);
    setCredCopied(true);
    setTimeout(() => setCredCopied(false), 2000);
  };

  const sendToClient = () => {
    if (!activationPanel) return;
    const subject = encodeURIComponent('Dine innloggingsdetaljer');
    const body = encodeURIComponent(
      `Hei,\n\nKontoen din hos AI Resepsjonen er nå aktivert.\n\n` +
      `Logg inn på: https://portal.airesepsjonen.no/login\n\n` +
      `E-post: ${activationPanel.email}\n` +
      `Passord: ${activationPanel.password}\n\n` +
      `Vi anbefaler at du endrer passordet ditt etter første innlogging under Innstillinger.\n\n` +
      `Vennlig hilsen,\nAI Resepsjonen`
    );
    window.location.href = `mailto:${activationPanel.email}?subject=${subject}&body=${body}`;
  };

  const pendingCount = tasks.filter(t => t.status !== 'complete' && t.status !== 'completed' && t.status !== 'done').length;

  return (
    <div data-testid="build-queue-page">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-accent-blue/20 rounded-lg flex items-center justify-center">
            <Wrench size={20} className="text-accent-blue" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl font-bold text-white tracking-tight">Build Queue</h1>
              {pendingCount > 0 && (
                <span className="text-xs px-2 py-1 bg-accent-yellow/20 text-accent-yellow rounded-full font-mono font-semibold">
                  {pendingCount} venter
                </span>
              )}
            </div>
            <p className="text-slate-500 text-sm mt-0.5">Provisjoneringsoppgaver som venter på fullføring</p>
          </div>
        </div>
      </div>

      {activationPanel && (
        <div className="mb-6 bg-accent-green/10 border border-accent-green/40 rounded-lg p-5" data-testid="activation-panel">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
                ✅ Klient aktivert!
              </h3>
              <p className="text-sm text-slate-300 mt-1">{activationPanel.company_name} er nå klar — del innloggingsdetaljene under med klienten.</p>
            </div>
            <button onClick={() => setActivationPanel(null)} className="text-slate-400 hover:text-white" aria-label="Lukk">
              <X size={18} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-1">E-post</div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono text-white break-all">{activationPanel.email}</span>
                <button onClick={() => copyCred(activationPanel.email)} className="text-slate-400 hover:text-white" title="Kopier">
                  <Copy size={13} />
                </button>
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-1">Passord</div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono text-white">{activationPanel.password}</span>
                <button onClick={() => copyCred(activationPanel.password)} className="text-slate-400 hover:text-white" title="Kopier">
                  {credCopied ? <CheckCircle size={13} className="text-accent-green" /> : <Copy size={13} />}
                </button>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={sendToClient}
              data-testid="send-to-client-button"
              className="flex items-center gap-2 px-4 py-2 bg-accent-blue hover:bg-accent-blue-hover text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Mail size={14} /> Send til klient
            </button>
          </div>
        </div>
      )}

      {notAvailable ? (
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-10 text-center">
          <Inbox size={28} className="mx-auto text-slate-600 mb-3" />
          <p className="text-sm text-slate-400">Build queue endepunktet er ikke klart ennå.</p>
          <p className="text-xs text-slate-600 mt-1">Denne siden aktiveres når backend-endepunktet er tilgjengelig.</p>
        </div>
      ) : (
        <div className="bg-navy-800 border border-navy-600 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-navy-600">
                  <th className="w-8 px-3 py-3"></th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Bedrift</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Kontakt-epost</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Telefon</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Opprettet</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(4)].map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      {[...Array(7)].map((_, j) => (
                        <td key={j} className="px-4 py-4"><div className="h-4 bg-navy-700 rounded w-20" /></td>
                      ))}
                    </tr>
                  ))
                ) : tasks.length === 0 ? (
                  <tr><td colSpan="7" className="px-4 py-12 text-center text-slate-500">Ingen oppgaver i køen</td></tr>
                ) : tasks.map(task => {
                  const id = task._id || task.id;
                  const expanded = expandedId === id;
                  const status = task.status || 'pending';
                  const completed = status === 'complete' || status === 'completed' || status === 'done';
                  return (
                    <React.Fragment key={id}>
                      <tr
                        data-testid={`build-queue-row-${id}`}
                        className="border-b border-navy-600/50 hover:bg-navy-700/50 transition-colors"
                      >
                        <td className="px-3 py-3.5">
                          <button
                            onClick={() => toggleExpand(id)}
                            className="text-slate-500 hover:text-white transition-colors"
                            aria-label="Utvid"
                          >
                            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-sm font-medium text-white">{task.company_name || '—'}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-sm text-slate-400">{task.contact_email || '—'}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-sm text-slate-400 font-mono">{task.phone || task.contact_phone || '—'}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-sm text-slate-400">{task.created_at ? formatDate(task.created_at) : '—'}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className={`text-xs px-2 py-1 rounded ${
                            completed ? 'bg-accent-green/15 text-accent-green' :
                            status === 'in_progress' ? 'bg-accent-blue/15 text-accent-blue' :
                            'bg-accent-yellow/15 text-accent-yellow'
                          }`}>
                            {STATUS_LABELS[status] || status}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-right">
                          <button
                            onClick={() => toggleExpand(id)}
                            disabled={completed}
                            className="text-xs px-3 py-1.5 border border-navy-600 rounded-lg text-slate-300 hover:border-accent-blue hover:text-white transition-colors disabled:opacity-30"
                          >
                            {expanded ? 'Lukk' : 'Åpne'}
                          </button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="bg-navy-900/50 border-b border-navy-600/50">
                          <td colSpan="7" className="px-6 py-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                              <div>
                                <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Retell Agent ID</label>
                                <input
                                  type="text"
                                  value={rowForm[id]?.retell_agent_id || ''}
                                  onChange={(e) => updateField(id, 'retell_agent_id', e.target.value)}
                                  placeholder="agent_xxxxxxxxxxxxxxxxxxxxx"
                                  className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue font-mono"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Tildelt nummer</label>
                                <input
                                  type="text"
                                  value={rowForm[id]?.assigned_phone || ''}
                                  onChange={(e) => updateField(id, 'assigned_phone', e.target.value)}
                                  placeholder="+47 XXX XX XXX"
                                  className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue font-mono"
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Notater</label>
                                <textarea
                                  rows={3}
                                  value={rowForm[id]?.notes || ''}
                                  onChange={(e) => updateField(id, 'notes', e.target.value)}
                                  placeholder="Eventuelle notater om provisjoneringen..."
                                  className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue resize-none"
                                />
                              </div>
                            </div>
                            <div className="mt-4 flex items-center justify-end gap-2">
                              <button
                                onClick={() => setExpandedId(null)}
                                className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
                              >
                                Avbryt
                              </button>
                              <button
                                onClick={() => handleComplete(id)}
                                disabled={completing === id}
                                className="flex items-center gap-2 px-4 py-2 bg-accent-green hover:bg-accent-green/80 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                              >
                                {completing === id ? <Loader size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                                Marker fullført
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
